import type { EventItem } from "types/user";
import type { AssignedSeatingMap } from "./types";

export const shouldFetchAssignedSeatingMap = ({
  event,
}: {
  event?: EventItem;
}) => isAssignedSeatingBuyerEvent({ event });

export const shouldWaitForAssignedSeatingMap = ({
  event,
  isLoading,
}: {
  event?: EventItem;
  isLoading: boolean;
}) => shouldFetchAssignedSeatingMap({ event }) && isLoading;

export const getAssignedSeatingMapEventId = ({
  event,
  eventId,
}: {
  event?: EventItem;
  eventId?: string;
}) => eventId || event?.offerSlug;

export const isAssignedSeatingBuyerEvent = ({
  event,
}: {
  event?: EventItem;
  seatMap?: AssignedSeatingMap | null;
}): boolean => {
  if (!event) {
    return false;
  }

  // Both public and authenticated event responses declare this server-owned mode.
  // A missing or delayed map must not change the event's purchase flow.
  switch (event.ticketingType) {
    case "reserved":
      return true;
    case "general":
      return false;
    default:
      throw new Error("Event response must declare ticketingType");
  }
};

export const loadAssignedSeatingMap = async (
  loadMap: () => Promise<AssignedSeatingMap>
): Promise<AssignedSeatingMap | null> => {
  try {
    return await loadMap();
  } catch (error) {
    if ((error as { status?: unknown })?.status === 404) {
      return null;
    }

    throw error;
  }
};

export const publicAssignedSeatingMapUrl = ({
  restUrl,
  eventId,
}: {
  restUrl?: string;
  eventId?: string;
}) => `${restUrl}/offers/v1/event-offer/seating/events/${eventId}`;

export const loadPublicAssignedSeatingMap = async ({
  event,
  eventId,
  restUrl,
  fetchMap = fetch,
}: {
  event?: EventItem;
  eventId?: string;
  restUrl?: string;
  fetchMap?: typeof fetch;
}): Promise<AssignedSeatingMap | null> => {
  if (!shouldFetchAssignedSeatingMap({ event }) || !eventId || !restUrl) {
    return null;
  }

  try {
    const response = await fetchMap(
      publicAssignedSeatingMapUrl({ restUrl, eventId })
    );

    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
};
